import React, { FunctionComponent } from 'react'
import { observer } from 'mobx-react-lite'

import { Box, Grid } from '@material-ui/core'

import { ImportStatusTable } from './ImportStatusTable'
import { ImportStocksForm, ImportStocksFormProps } from './ImportStocksForm'
import { ImportProductsForm, ImportProductsFormProps } from './ImportProductsForm'

import { ServerTableData } from '../store/serverTableData'

import { EnumConstraint } from '../common/tsUtils'

import { AccountProps, StoreAccountModel } from '../types/accountProps'
import { IntegrationTypeKey } from '../types/integrationTypeUtils'

import { StoreProductModel } from '../server/mpsklad_core/Models/StoreProductModel'

export type ImportProductsProps<
  TAccount extends StoreAccountModel = never,
  TProducts extends ServerTableData<TRow, TColumnId, TFilterEnum, never> = never,
  TRow extends StoreProductModel = never,
  TColumnId extends string = never,
  TFilterEnum extends EnumConstraint<TFilterEnum> & {hasMsProduct: unknown} = never> = {
  account: TAccount

  products: TProducts
}

export type ImportScreenProps<
  TAccount extends StoreAccountModel = never,
  TProducts extends ServerTableData<TRow, TColumnId, TFilterEnum, never> = never,
  TRow extends StoreProductModel = never,
  TColumnId extends string = never,
  TFilterEnum extends EnumConstraint<TFilterEnum> & {hasMsProduct: unknown} = never> =
  AccountProps<TAccount>
  & ImportProductsFormProps<TProducts, TRow, TColumnId, TFilterEnum>
  & ImportStocksFormProps<TProducts, TRow, TColumnId, TFilterEnum>
  & ImportProductsProps<TAccount, TProducts, TRow, TColumnId, TFilterEnum>
  & {
  type: Exclude<IntegrationTypeKey, 'MoySklad'>

  TableComponent: FunctionComponent<ImportProductsProps<TAccount, TProducts, TRow, TColumnId, TFilterEnum>>
}

export const ImportScreen =
  observer(
    <TAccount extends StoreAccountModel = never,
      TProducts extends ServerTableData<TRow, TColumnId, TFilterEnum, never> = never,
      TRow extends StoreProductModel = never,
      TColumnId extends string = never,
      TFilterEnum extends EnumConstraint<TFilterEnum> & {hasMsProduct: unknown} = never>
    ({
       account,
       type,
       addProductsFunc,
       updateProductsFunc,
       importStocksFunc,
       products,
       TableComponent
     }: ImportScreenProps<TAccount, TProducts, TRow, TColumnId, TFilterEnum>) =>
      <Box>
        <Grid container direction="column" spacing={2}>
          <Grid item lg={10} xl={3} xs={6}>
            <ImportStatusTable type={type} states={account.syncStates}/>
          </Grid>

          <Grid item lg={10} xl={8}>
            <ImportProductsForm<TProducts, TRow, TColumnId, TFilterEnum>
              account={account}
              addProductsFunc={addProductsFunc}
              updateProductsFunc={updateProductsFunc}
              products={products}
            />
          </Grid>

          <Grid item lg={10} xl={8}>
            <ImportStocksForm<TProducts, TRow, TColumnId, TFilterEnum>
              account={account}
              importStocksFunc={importStocksFunc}
              products={products}
            />
          </Grid>

          <Grid item lg={10} xl={8}>
            <Box style={{width: 'calc(100vw - 350px)'}}>
              <TableComponent account={account} products={products}/>
            </Box>
          </Grid>
        </Grid>
      </Box>
  )